import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import ParallaxBanner from '../components/ParallaxBanner';
import contentService from '../services/contentService';
import featureFlags from '../utils/featureFlags';
import useContentUpdates from '../hooks/useContentUpdates';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { useAuth } from '../hooks/useAuth';

function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, loading, error, isAuthenticated } = useAuth();

  // Get redirect destination from state OR query parameter
  const getRedirectUrl = () => {
    // First check location state (passed from protected routes)
    if (location.state?.from) {
      return location.state.from;
    }
    // Then check query parameter (passed from review form, etc.)
    const params = new URLSearchParams(location.search);
    const redirectParam = params.get('redirect');
    if (redirectParam) {
      return decodeURIComponent(redirectParam);
    }
    // Default to home
    return '/';
  };

  const from = getRedirectUrl();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    rememberMe: false
  });
  const [formError, setFormError] = useState('');

  // Handle Google Sign-Up success
  const handleGoogleSuccess = () => {
    navigate(from, { replace: true });
  };

  // Handle Google Sign-Up error
  const handleGoogleError = (errorMessage) => {
    setFormError(errorMessage);
  };

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);
  useEffect(() => {
    // Handle window load event (fade out loader)
    const handleWindowLoad = () => {
      if (window.$ && window.$('.loader1').length > 0) {
        window.$(".loader1").fadeOut(800);
        window.$('.side-menu').removeClass('hidden');
      }
    };

    // If window is already loaded, trigger immediately
    if (document.readyState === 'complete') {
      handleWindowLoad();
    } else {
      window.addEventListener('load', handleWindowLoad);
    }

    return () => {
      window.removeEventListener('load', handleWindowLoad);
    };
  }, []);


  const [backgroundUrl, setBackgroundUrl] = useState(null);

  const fetchBackground = useCallback(async () => {
    if (!featureFlags.useDynamicBackgrounds()) {
      return;
    }
    try {
      const result = await contentService.getPageBackground('product-listing');
      if (result.success && result.imageUrl) {
        setBackgroundUrl(result.imageUrl);
      }
    } catch (err) {
      console.error('[Register] Error fetching background:', err);
    }
  }, []);

  useEffect(() => {
    fetchBackground();
  }, [fetchBackground]);

  useContentUpdates({
    onPageBackgroundUpdated: (page) => {
      if (page === 'product-listing') {
        fetchBackground();
      }
    }
  });

  return (
    <Layout>
      {/* Parallax Banner - Using banner6.jpg for register page */}
      {backgroundUrl && (
        <ParallaxBanner
          imageUrl={backgroundUrl}
          speed={0.6}
          offset={0}
          className="paralax-section-slide-data"
        />
      )}

      {/* START MAIN CONTENT - Register Form */}
      <div className="signup-form">

        {/* START MANAGE CONTENT */}
        <div className="about_content">

          <div className="container">
            <div className="row">
              <div className="col-12 col-lg-8 offset-lg-2 text-center wow slideInUp">
                <h1 className="heading">Registeration</h1>
                <p className="para_text mt-3">
                  Lorem ipsum dolor sit amet, consectetur adipisicing elit. A dolores explicabo laudantium, omnis provident quam reiciendis voluptatum?
                </p>
              </div>
            </div>
          </div>

          {/* START FORM SECTION */}
          <div className="row no-gutters">

            {/* Left Column - Registration Form */}
            <div className="col-lg-6 col-md-12 col-sm-12 pr-lg-0 whitebox wow slideInLeft">
              <div className="widget logincontainer register-account">
                <h3 className="bottom35 text-center text-md-left">Create Your Account</h3>

                {/* Google Sign-Up Button */}
                <div className="google-auth-section mb-4">
                  <GoogleSignInButton
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    buttonText="signup_with"
                    buttonId="google-signin-register"
                  />
                </div>

                {/* Divider */}
                <div className="auth-divider mb-4">
                  <span>or register with email</span>
                </div>

                <form className="getin_form border-form" id="register" onSubmit={async (e) => {
                  e.preventDefault();
                  setFormError('');

                  // Validation
                  if (!formData.fullName || !formData.email || !formData.password || !formData.confirmPassword) {
                    setFormError('Please fill in all fields');
                    return;
                  }

                  if (formData.password !== formData.confirmPassword) {
                    setFormError('Passwords do not match');
                    return;
                  }

                  if (formData.password.length < 8) {
                    setFormError('Password must be at least 8 characters');
                    return;
                  }

                  // Split full name into first and last
                  const nameParts = formData.fullName.trim().split(' ');
                  const firstName = nameParts[0] || '';
                  const lastName = nameParts.slice(1).join(' ') || nameParts[0];

                  const result = await register({
                    email: formData.email,
                    password: formData.password,
                    firstName,
                    lastName,
                    marketingConsent: false
                  });

                  if (result.success) {
                    navigate(from, { replace: true });
                  } else {
                    setFormError(result.error || 'Registration failed');
                  }
                }}>
                  <div className="row">

                    {/* Error Message */}
                    {(formError || error) && (
                      <div className="col-md-12 col-sm-12">
                        <div className="alert alert-danger" role="alert">
                          {formError || error}
                        </div>
                      </div>
                    )}

                    <div className="col-md-12 col-sm-12">
                      <div className="form-group bottom35">
                        <label htmlFor="registerName" className="d-none"></label>
                        <input
                          className="form-control"
                          type="text"
                          placeholder="Full Name:"
                          required
                          id="registerName"
                          value={formData.fullName}
                          onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                          disabled={loading}
                        />
                      </div>
                    </div>

                    <div className="col-md-12 col-sm-12">
                      <div className="form-group bottom35">
                        <label htmlFor="registerEmail" className="d-none"></label>
                        <input
                          className="form-control"
                          type="email"
                          placeholder="Email:"
                          required
                          id="registerEmail"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          disabled={loading}
                        />
                      </div>
                    </div>

                    <div className="col-md-12 col-sm-12">
                      <div className="form-group bottom35">
                        <label htmlFor="registerPass" className="d-none"></label>
                        <input
                          className="form-control"
                          type="password"
                          placeholder="Password: (min 8 chars, 1 uppercase, 1 lowercase, 1 number)"
                          required
                          id="registerPass"
                          value={formData.password}
                          onChange={(e) => setFormData({...formData, password: e.target.value})}
                          disabled={loading}
                        />
                      </div>
                    </div>

                    <div className="col-md-12 col-sm-12">
                      <div className="form-group bottom35">
                        <label htmlFor="registerPassConfirm" className="d-none"></label>
                        <input
                          className="form-control"
                          type="password"
                          placeholder="Confirm Password:"
                          required
                          id="registerPassConfirm"
                          value={formData.confirmPassword}
                          onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                          disabled={loading}
                        />
                      </div>
                    </div>

                    <div className="col-md-12 col-sm-12">
                      <div className="form-group bottom35">
                        <div className="form-check text-left">
                          <div className="custom-control custom-checkbox">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id="defaultUnchecked"
                              checked={formData.rememberMe}
                              onChange={(e) => setFormData({...formData, rememberMe: e.target.checked})}
                              disabled={loading}
                            />
                            <label className="custom-control-label register-remember" htmlFor="defaultUnchecked">
                              Remember me on this device
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-12 register-btn">
                      <button type="submit" className="btn pink-gradient-btn-into-black" disabled={loading}>
                        {loading ? 'Creating Account...' : 'Register'}
                      </button>
                      <p className="top20 SignInclass">
                        Already have an account? &nbsp;<a href="/login">Sign In</a>
                      </p>
                    </div>

                  </div>
                </form>
              </div>
            </div>

            {/* Right Column - Benefits */}
            <div className="col-lg-6 col-md-12 col-sm-12 registeration-detail wow slideInRight text-center text-md-left" data-wow-delay=".9s">
              <div className="heading-area pl-lg-4 p-0">
                <h6 className="sub-title text-left">Basic info about Registration</h6>
                <h2 className="title">Benefit of Registration</h2>
                <p className="paragraph">There are many variations of passages of Lorem Ipsum available be the majority have suffered alteration in some form, by injected humour or randomised words.</p>
                <ul className="about-list">

                    <li><i className="lni-check-mark-circle a-icon  mr-lg-3 mr-sm-2"></i> <span>Lorem ipsum dolor amet, consectetur adipiscing.</span></li>
                    <li><i className="lni-check-mark-circle a-icon  mr-lg-3 mr-sm-2"></i> <span>Morbi ornare nibh id cursus vestibulum.</span></li>
                    <li><i className="lni-check-mark-circle a-icon  mr-lg-3 mr-sm-3"></i> <span>Duis vitae lectus facilisis, tristique lorem sit amet males.</span></li>
                    <li><i className="lni-check-mark-circle a-icon  mr-lg-3 mr-sm-2"></i> <span>It is a long established fact that a distracted</span></li>
                    <li><i className="lni-check-mark-circle a-icon  mr-lg-3 mr-sm-2"></i> <span>Morbi ornare nibh id cursus vestibulum.</span></li>
                    <li><i className="lni-check-mark-circle a-icon  mr-lg-3 mr-sm-2"></i> <span>It is a Lorem ipsum dolor fact  cursus vestibulum.</span></li>

                </ul>
              </div>
            </div>

          </div>
          {/* END FORM SECTION */}

        </div>
        {/* END MANAGE CONTENT */}

      </div>
      {/* END MAIN CONTENT */}
    </Layout>
  );
}

export default Register;
